import { markRaw, onUnmounted, ref, shallowRef } from 'vue'
import { AudioPresets, DisconnectReason, isBrowserSupported, Room, RoomEvent, setLogLevel, Track, VideoPresets43 } from 'livekit-client'

import type { Ref, ShallowRef } from 'vue'
import type { LocalTrackPublication } from 'livekit-client'

import { useVoipRoomStore } from '@stores/voip-room'

import { createNoiseSuppressionProcessor, preloadNoiseSuppression } from '@utils/classroom/noise-suppression'

export type RoomLiveKitState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected'

type NoiseSuppressionProcessor = ReturnType<typeof createNoiseSuppressionProcessor>

interface UseRoomLiveKitOptions {
  bindLifecycle?: boolean
}

interface UseRoomLiveKitReturn {
  connectionState: Ref<RoomLiveKitState>
  localIdentity: Ref<string | null>
  remoteIdentities: Ref<string[]>
  localName: Ref<string | null>
  remoteName: Ref<string | null>
  lastDisconnectReason: Ref<string | null>
  canPlaybackAudio: Ref<boolean>
  isSupported: boolean
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  startAudio: () => Promise<void>
}

if (import.meta.dev) {
  setLogLevel('debug')
}

export const refRoom: ShallowRef<Room | null> = shallowRef(null)

const connectionState = ref<RoomLiveKitState>('idle')
const localIdentity = ref<string | null>(null)
const remoteIdentities = ref<string[]>([])
const localName = ref<string | null>(null)
const remoteName = ref<string | null>(null)
const lastDisconnectReason = ref<string | null>(null)
const canPlaybackAudio = ref<boolean>(true)

let pendingDisconnect: Promise<void> | null = null

const syncRemoteParticipants = (room: Room): void => {
  const remotes = Array.from(room.remoteParticipants.values())
  remoteIdentities.value = remotes.map((participant) => participant.identity)
  const [firstRemote] = remotes
  remoteName.value = firstRemote ? firstRemote.name || firstRemote.identity : null
}

const handleDisconnected = (room: Room, reason?: DisconnectReason): void => {
  if (refRoom.value !== room) return

  connectionState.value = 'disconnected'
  lastDisconnectReason.value = reason !== undefined ? DisconnectReason[reason] ?? 'UNKNOWN' : null
  localIdentity.value = null
  remoteIdentities.value = []
  localName.value = null
  remoteName.value = null
  canPlaybackAudio.value = true
  refRoom.value = null
}

const applyNoiseSuppression = async (
  publication: LocalTrackPublication,
  processor: NoiseSuppressionProcessor,
): Promise<void> => {
  const track = publication.audioTrack
  if (publication.source !== Track.Source.Microphone || !track) return
  if (track.getProcessor() === processor) return

  try {
    await track.setProcessor(processor)
  } catch (error) {
    console.warn('[classroom] failed to apply noise suppression, publishing the raw microphone', error)
  }
}

const hasLiveRoom = (): boolean =>
  !!refRoom.value
  && (connectionState.value === 'connecting'
    || connectionState.value === 'connected'
    || connectionState.value === 'reconnecting')

const connect = async (): Promise<void> => {
  if (!isBrowserSupported()) {
    connectionState.value = 'disconnected'
    lastDisconnectReason.value = 'UNSUPPORTED_BROWSER'
    console.warn('[classroom] browser does not support LiveKit / WebRTC')
    return
  }
  if (pendingDisconnect) {
    await pendingDisconnect
  }
  if (hasLiveRoom()) return

  const voipRoomStore = useVoipRoomStore()
  const roomData = voipRoomStore.getRoomData
  if (!roomData?.ws_url || !roomData?.token) {
    connectionState.value = 'disconnected'
    lastDisconnectReason.value = 'TOKEN_MISSING'
    console.warn('missing LiveKit room data for classroom/room-live-kit.hook.ts')
    return
  }

  connectionState.value = 'connecting'
  lastDisconnectReason.value = null

  const room = markRaw(new Room({
    disconnectOnPageLeave: true,
    adaptiveStream: { pauseVideoInBackground: false },
    dynacast: true,
    videoCaptureDefaults: {
      resolution: VideoPresets43.h720.resolution,
    },
    publishDefaults: {
      videoCodec: 'vp8',
      videoEncoding: VideoPresets43.h720.encoding,
      simulcast: true,
      videoSimulcastLayers: [VideoPresets43.h180, VideoPresets43.h360],
      screenShareEncoding: { maxBitrate: 1_500_000, maxFramerate: 10 },
      audioPreset: AudioPresets.speech,
    },
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      voiceIsolation: false,
    },
  }))
  const noiseSuppression = createNoiseSuppressionProcessor()
  void preloadNoiseSuppression()

  room
    .on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
      void applyNoiseSuppression(publication, noiseSuppression)
    })
    .on(RoomEvent.Reconnecting, () => {
      connectionState.value = 'reconnecting'
    })
    .on(RoomEvent.Reconnected, () => {
      connectionState.value = 'connected'
      syncRemoteParticipants(room)
    })
    .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      handleDisconnected(room, reason)
    })
    .on(RoomEvent.ParticipantConnected, () => {
      syncRemoteParticipants(room)
    })
    .on(RoomEvent.ParticipantDisconnected, () => {
      syncRemoteParticipants(room)
    })
    .on(RoomEvent.AudioPlaybackStatusChanged, () => {
      canPlaybackAudio.value = room.canPlaybackAudio
    })

  refRoom.value = room

  try {
    await room.connect(roomData.ws_url, roomData.token)
  } catch (error) {
    if (refRoom.value === room) {
      connectionState.value = 'disconnected'
      lastDisconnectReason.value = 'CONNECT_FAILED'
      refRoom.value = null
    }
    console.error('[classroom] failed to connect', error)
    return
  }

  // Superseded during the await — abandon this room so it can't keep publishing.
  if (refRoom.value !== room) {
    await room.disconnect().catch((error) => {
      console.error('[classroom] failed to disconnect superseded room', error)
    })
    return
  }

  connectionState.value = 'connected'
  localIdentity.value = room.localParticipant.identity
  localName.value = room.localParticipant.name || room.localParticipant.identity
  canPlaybackAudio.value = room.canPlaybackAudio
  syncRemoteParticipants(room)

  void room.startAudio().catch(() => {})
}

const disconnect = async (): Promise<void> => {
  const room = refRoom.value
  if (!room) return

  pendingDisconnect = room
    .disconnect()
    .catch((error) => {
      console.error('[classroom] disconnect failed', error)
    })
    .finally(() => {
      pendingDisconnect = null
    })

  await pendingDisconnect
}

const startAudio = async (): Promise<void> => {
  const room = refRoom.value
  if (!room) return

  try {
    await room.startAudio()
  } catch (error) {
    console.error('[classroom] failed to start audio', error)
  } finally {
    canPlaybackAudio.value = room.canPlaybackAudio
  }
}

export const useRoomLiveKit = (options: UseRoomLiveKitOptions = {}): UseRoomLiveKitReturn => {
  if (options.bindLifecycle) {
    onUnmounted(() => {
      void disconnect()
    })
  }

  return {
    connectionState,
    localIdentity,
    remoteIdentities,
    localName,
    remoteName,
    lastDisconnectReason,
    canPlaybackAudio,
    isSupported: isBrowserSupported(),
    connect,
    disconnect,
    startAudio,
  }
}
